/**
 * JKL Cart JavaScript
 * Matches Hydrogen cart functionality (CartLineItem, CartLineQuantity, CartAside)
 *
 * Features:
 * - Cart drawer open/close
 * - Quantity update with debouncing (prevents race conditions)
 * - Remove item
 * - Add to cart from recommendations
 * - Free shipping progress bar updates
 */

(function() {
  'use strict';

  // Configuration
  const DEBOUNCE_DELAY = 500; // Matches Hydrogen CartLineQuantity DEBOUNCE_DELAY
  const MIN_REQUEST_INTERVAL = 1000; // Minimum time between API requests (rate limit protection)
  const CART_ROUTES = {
    change: window.routes?.cart_change_url || '/cart/change.js',
    add: window.routes?.cart_add_url || '/cart/add.js',
    get: window.routes?.cart_url || '/cart.js'
  };

  /**
   * Cart lines that must not change displayed subtotal/total (OMAGGIO, promos, gift cards).
   * Matches jkl-cart-gift-promo / Hydrogen calculateSubtotal exclusions.
   */
  function getLineItemType(item) {
    const p = item.properties;
    if (p == null) return null;
    if (typeof p === 'object' && !Array.isArray(p)) {
      return p._type || null;
    }
    if (Array.isArray(p)) {
      for (let i = 0; i < p.length; i++) {
        if (p[i].name === '_type') return p[i].value;
      }
    }
    return null;
  }

  /** Regios Discounts auto-gift lines — same keys as Liquid jkl-cart-drawer / jkl-main-cart. */
  function itemHasRegiosAutoGiftProperties(item) {
    const p = item.properties;
    if (p == null) return false;
    if (typeof p === 'object' && !Array.isArray(p)) {
      return (
        Object.prototype.hasOwnProperty.call(p, '_regios_auto_added_by_discount_id') ||
        Object.prototype.hasOwnProperty.call(p, '_regios_auto_add_to_cart_node_id')
      );
    }
    if (Array.isArray(p)) {
      for (let i = 0; i < p.length; i++) {
        const name = p[i].name != null ? p[i].name : p[i].first;
        if (
          name === '_regios_auto_added_by_discount_id' ||
          name === '_regios_auto_add_to_cart_node_id'
        ) {
          return true;
        }
      }
    }
    return false;
  }

  function isJklLineExcludedFromDisplayedTotals(item) {
    if (!item) return false;
    if (item.gift_card) return true;
    if (itemHasRegiosAutoGiftProperties(item)) return true;
    const t = getLineItemType(item);
    return t === 'gift' || t === 'giftThreshold' || t === 'giftProduct' || t === 'promo';
  }

  function getJklDisplayedCartTotals(cart) {
    let excludedFinal = 0;
    let excludedOriginal = 0;
    (cart.items || []).forEach((item) => {
      if (!isJklLineExcludedFromDisplayedTotals(item)) return;
      excludedFinal += item.final_line_price || 0;
      const orig =
        item.original_line_price != null ? item.original_line_price : item.final_line_price || 0;
      excludedOriginal += orig;
    });

    // Gift-twin adjustment: a regular item may be discounted to €0 by an external app
    // (e.g. Supaeasy) because a giftProduct for the same variant is in the cart.
    // Add back that discount so the displayed total matches checkout behavior.
    const giftProductVariantIds = new Set(
      (cart.items || [])
        .filter((i) => getLineItemType(i) === 'giftProduct')
        .map((i) => i.variant_id)
    );
    let giftTwinAdj = 0;
    (cart.items || []).forEach((item) => {
      if (isJklLineExcludedFromDisplayedTotals(item)) return;
      if (giftProductVariantIds.has(item.variant_id)) {
        const orig = item.original_line_price != null ? item.original_line_price : 0;
        const final = item.final_line_price || 0;
        if (final < orig) giftTwinAdj += orig - final;
      }
    });

    const displayTotal = Math.max(0, (cart.total_price || 0) - excludedFinal + giftTwinAdj);
    const displaySubtotal = Math.max(0, (cart.original_total_price || 0) - excludedOriginal);
    return { displayTotal, displaySubtotal };
  }

  /** Eligible merchandise value in cents (for free-shipping bar), same rules as gift promo. */
  function getJklEligibleCartValueCents(cart) {
    let sum = 0;
    (cart.items || []).forEach((item) => {
      if (isJklLineExcludedFromDisplayedTotals(item)) return;
      sum += item.final_line_price || 0;
    });
    return sum;
  }

  /**
   * Parse metaobject thresholds string "50:1,80:2,100:4" → [{ amountCents, count }]
   */
  function parseGiftThresholdsString(raw) {
    if (!raw || typeof raw !== 'string') return [];
    const out = [];
    raw.split(',').forEach((pair) => {
      const parts = pair.trim().split(':');
      if (parts.length >= 2) {
        const amountCents = Math.round(parseFloat(parts[0], 10) * 100);
        const count = parseInt(parts[1], 10);
        if (!Number.isNaN(amountCents) && !Number.isNaN(count)) {
          out.push({ amountCents, count });
        }
      }
    });
    return out;
  }

  /** Highest tier satisfied: max count among pairs where eligibleCents >= amount (same as Liquid gift promo). */
  function maxGiftsAllowedForEligibleCents(eligibleCents, pairs) {
    let bestAmount = -1;
    let giftCount = 0;
    pairs.forEach(({ amountCents, count }) => {
      if (eligibleCents >= amountCents && amountCents > bestAmount) {
        bestAmount = amountCents;
        giftCount = count;
      }
    });
    return giftCount;
  }

  /**
   * Remove giftThreshold lines if eligible subtotal no longer allows them (cart total dropped below tier).
   * Keeps the first N gift lines in cart order, removes the rest; if max is 0, removes all.
   * Returns Promise<{ removed: boolean, cart: object }>
   */
  function tryRemoveExcessGiftThresholdLines(cart) {
    const cfg = window.jklGiftThresholdConfig;
    if (!cfg || !cfg.enabled || !cfg.thresholds) {
      return Promise.resolve({ removed: false, cart });
    }
    const pairs = parseGiftThresholdsString(cfg.thresholds);
    if (pairs.length === 0) {
      return Promise.resolve({ removed: false, cart });
    }

    const eligible = getJklEligibleCartValueCents(cart);
    const maxGifts = maxGiftsAllowedForEligibleCents(eligible, pairs);

    const giftKeysInOrder = [];
    (cart.items || []).forEach((item) => {
      if (getLineItemType(item) === 'giftThreshold') {
        giftKeysInOrder.push(item.key);
      }
    });

    let keysToRemove = [];
    if (maxGifts === 0) {
      keysToRemove = giftKeysInOrder.slice();
    } else if (giftKeysInOrder.length > maxGifts) {
      keysToRemove = giftKeysInOrder.slice(maxGifts);
    }

    if (keysToRemove.length === 0) {
      return Promise.resolve({ removed: false, cart });
    }

    const updates = {};
    keysToRemove.forEach((k) => {
      updates[k] = 0;
    });

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ updates })
    }).then((res) => {
      if (!res.ok) return { removed: false, cart };
      return res.json().then((newCart) => ({ removed: true, cart: newCart }));
    });
  }

  /**
   * Remove giftProduct lines if the category-based promo conditions are no longer met.
   * Uses window.jklGiftProductPromoConfig (set by jkl-main-cart.liquid on page load).
   * If a cart item's product is unknown (added after page load), we are conservative
   * and do NOT remove gift lines — Liquid re-render handles the display state.
   * Returns Promise<{ removed: boolean, cart: object }>
   */
  function tryRemoveGiftProductLines(cart) {
    const cfg = window.jklGiftProductPromoConfig;
    if (!cfg || !cfg.enabled) return Promise.resolve({ removed: false, cart });

    const giftLines = (cart.items || []).filter(
      (item) => getLineItemType(item) === 'giftProduct'
    );
    if (giftLines.length === 0) return Promise.resolve({ removed: false, cart });

    const mapping = cfg.cartItemCollections || {};
    const conditions = Array.isArray(cfg.conditions) ? cfg.conditions : [];
    if (conditions.length === 0) return Promise.resolve({ removed: false, cart });

    // Build set of known collection handles from current non-gift cart items
    let hasUnknownProduct = false;
    const cartCollHandles = new Set();
    (cart.items || []).forEach((item) => {
      if (isJklLineExcludedFromDisplayedTotals(item)) return;
      const pid = String(item.product_id);
      if (mapping[pid]) {
        mapping[pid].forEach((h) => cartCollHandles.add(h));
      } else {
        // Product added after page load — collection membership unknown
        hasUnknownProduct = true;
      }
    });

    // Be conservative: if there's an unknown product, skip enforcement
    if (hasUnknownProduct) return Promise.resolve({ removed: false, cart });

    const anyRuleMet = conditions.some((h) => cartCollHandles.has(h));

    if (anyRuleMet) return Promise.resolve({ removed: false, cart });

    // No rule met — remove all giftProduct lines
    const updates = {};
    giftLines.forEach((item) => { updates[item.key] = 0; });

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates })
    }).then((res) => {
      if (!res.ok) return { removed: false, cart };
      return res.json().then((newCart) => ({ removed: true, cart: newCart }));
    });
  }

  function initGiftThresholdEnforcementOnLoad() {
    if (!document.querySelector('[data-jkl-cart-page]')) return;

    fetch(CART_ROUTES.get, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => tryRemoveExcessGiftThresholdLines(cart))
      .then(({ removed, cart }) => {
        if (removed) { window.location.reload(); return Promise.reject('reloaded'); }
        return tryRemoveGiftProductLines(cart);
      })
      .then(({ removed }) => {
        if (removed) window.location.reload();
      })
      .catch(() => {});
  }

  // State management
  let isUpdating = false;
  let pendingUpdates = new Map();
  let debounceTimers = new Map();
  let lastRequestTime = 0; // Track last API request time for rate limiting

  /**
   * Initialize JKL Cart functionality
   */
  function init() {
    // Cart drawer controls
    initCartDrawer();

    // Quantity controls
    initQuantityControls();

    // Remove buttons
    initRemoveButtons();

    // Add to cart buttons (recommendations)
    initAddToCartButtons();

    // Drop gift-threshold (OMAGGIO) lines if subtotal fell below tier (same session / back navigation)
    initGiftThresholdEnforcementOnLoad();

    // Note: Close on link click is handled by event delegation in initCartDrawer
  }

  // =========================================================================
  // Cart Drawer
  // =========================================================================

  function initCartDrawer() {
    const overlay = document.querySelector('[data-jkl-cart-overlay]');

    // Use event delegation on document for open/close buttons
    // This works even if buttons are rendered before the script loads
    // Use capture phase to ensure we get the event before other handlers
    document.addEventListener('click', function(e) {
      // Check if clicked element or its parent has data-jkl-cart-open
      const openBtn = e.target.closest('[data-jkl-cart-open]');

      if (openBtn) {
        // On mobile, let the link navigate directly to /cart
        if (openBtn.closest('#jkl-mobile-header__cart')) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        openCartDrawer();
        return;
      }

      // Check if clicked element or its parent has data-jkl-cart-close
      const closeBtn = e.target.closest('[data-jkl-cart-close]');
      if (closeBtn) {
        e.preventDefault();
        closeCartDrawer();
        return;
      }

      // Check if clicked element or its parent has data-jkl-cart-close-on-click
      const closeOnClick = e.target.closest('[data-jkl-cart-close-on-click]');
      if (closeOnClick) {
        closeCartDrawer();
        return;
      }
    });

    // Close on overlay click
    if (overlay) {
      overlay.addEventListener('click', closeCartDrawer);
    }

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      const currentDrawer = document.querySelector('[data-jkl-cart-drawer]');
      if (e.key === 'Escape' && currentDrawer && currentDrawer.classList.contains('is-open')) {
        closeCartDrawer();
      }
    });

    // Listen for custom open event (from header cart icon)
    document.addEventListener('jkl:cart:open', openCartDrawer);
    document.addEventListener('jkl:cart:close', closeCartDrawer);
  }

  function openCartDrawer() {
    const drawer = document.querySelector('[data-jkl-cart-drawer]');
    const overlay = document.querySelector('[data-jkl-cart-overlay]');
    const openButtons = document.querySelectorAll('[data-jkl-cart-open]');

    if (drawer) {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    if (overlay) {
      overlay.classList.add('is-visible');
    }

    // Update aria-expanded on open buttons
    openButtons.forEach(btn => {
      btn.setAttribute('aria-expanded', 'true');
    });

    // Focus trap - focus the close button
    const closeButton = drawer?.querySelector('[data-jkl-cart-close]');
    if (closeButton) {
      closeButton.focus();
    }
  }

  function closeCartDrawer() {
    const drawer = document.querySelector('[data-jkl-cart-drawer]');
    const overlay = document.querySelector('[data-jkl-cart-overlay]');
    const openButtons = document.querySelectorAll('[data-jkl-cart-open]');

    if (drawer) {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    if (overlay) {
      overlay.classList.remove('is-visible');
    }

    // Update aria-expanded on open buttons
    openButtons.forEach(btn => {
      btn.setAttribute('aria-expanded', 'false');
    });
  }

  // =========================================================================
  // Quantity Controls - Matches Hydrogen CartLineQuantity with debouncing
  // =========================================================================

  function initQuantityControls() {
    // Decrease buttons
    document.querySelectorAll('[data-jkl-quantity-decrease]').forEach(btn => {
      btn.addEventListener('click', handleQuantityDecrease);
    });

    // Increase buttons
    document.querySelectorAll('[data-jkl-quantity-increase]').forEach(btn => {
      btn.addEventListener('click', handleQuantityIncrease);
    });
  }

  function handleQuantityDecrease(e) {
    const btn = e.currentTarget;
    const lineIndex = btn.dataset.lineIndex;
    const item = btn.closest('[data-jkl-cart-item]');
    const valueEl = item?.querySelector('[data-jkl-quantity-value]');

    if (!valueEl) return;

    const currentQty = parseInt(valueEl.textContent, 10);
    const newQty = Math.max(1, currentQty - 1);

    if (newQty !== currentQty) {
      updateQuantityWithDebounce(lineIndex, newQty, valueEl);
    }
  }

  function handleQuantityIncrease(e) {
    const btn = e.currentTarget;
    const lineIndex = btn.dataset.lineIndex;
    const item = btn.closest('[data-jkl-cart-item]');
    const valueEl = item?.querySelector('[data-jkl-quantity-value]');

    if (!valueEl) return;

    const currentQty = parseInt(valueEl.textContent, 10);
    const newQty = currentQty + 1;

    updateQuantityWithDebounce(lineIndex, newQty, valueEl);
  }

  /**
   * Update quantity with debouncing
   * Matches Hydrogen CartLineQuantity behavior:
   * - Shows immediate visual feedback
   * - Batches rapid clicks into single request
   * - Prevents race conditions
   */
  function updateQuantityWithDebounce(lineIndex, newQty, valueEl) {
    // Don't allow updates while another is in progress
    if (isUpdating) return;

    // Update display immediately for instant feedback
    valueEl.textContent = newQty;

    // Store pending update
    pendingUpdates.set(lineIndex, newQty);

    // Clear existing timer for this line
    if (debounceTimers.has(lineIndex)) {
      clearTimeout(debounceTimers.get(lineIndex));
    }

    // Set new debounce timer
    const timer = setTimeout(() => {
      const qty = pendingUpdates.get(lineIndex);
      if (qty !== undefined) {
        executeQuantityUpdate(lineIndex, qty);
        pendingUpdates.delete(lineIndex);
      }
      debounceTimers.delete(lineIndex);
    }, DEBOUNCE_DELAY);

    debounceTimers.set(lineIndex, timer);

    // Update button states
    updateButtonStates(lineIndex, newQty);
  }

  function executeQuantityUpdate(lineIndex, quantity) {
    // Rate limit protection: ensure minimum interval between requests
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime;

    if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
      // Delay the request to respect rate limits
      const delay = MIN_REQUEST_INTERVAL - timeSinceLastRequest;
      setTimeout(() => executeQuantityUpdate(lineIndex, quantity), delay);
      return;
    }

    isUpdating = true;
    setLoadingState(true, lineIndex);
    lastRequestTime = Date.now();

    // Get sections to render - similar to theme's cart.js approach
    const sectionsToRender = getSectionsToRender();

    fetch(CART_ROUTES.change, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        line: parseInt(lineIndex, 10),
        quantity: quantity,
        sections: sectionsToRender.map(s => s.section),
        sections_url: window.location.pathname
      })
    })
    .then(response => {
      // Check for rate limiting
      if (response.status === 429) {
        setTimeout(() => {
          isUpdating = false;
          executeQuantityUpdate(lineIndex, quantity);
        }, 2000);
        return null;
      }
      return response.json();
    })
    .then(state => {
      if (!state) return; // Rate limited, retry scheduled

      if (state.item_count === 0) {
        window.location.reload();
        return;
      }

      const applySectionPatch = () => {
        if (state.sections && sectionsToRender.length > 0) {
          const sectionId = sectionsToRender[0].section;
          const sectionHtml = state.sections[sectionId];

          if (sectionHtml) {
            const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');

            sectionsToRender.forEach((section) => {
              const newElement = doc.querySelector(section.selector);
              const currentElement = document.querySelector(section.selector);

              if (newElement && currentElement) {
                currentElement.innerHTML = newElement.innerHTML;
              }
            });
          }

          initQuantityControls();
          initRemoveButtons();
          initAddToCartButtons();
        }

        updateCartCount(state.item_count);
      };

      const cartPromise = fetch(CART_ROUTES.get, {
        headers: { Accept: 'application/json' }
      }).then((r) => r.json());

      cartPromise
        .then((cart) => tryRemoveExcessGiftThresholdLines(cart))
        .then(({ removed, cart }) => {
          if (removed) { window.location.reload(); return Promise.reject('reloaded'); }
          return tryRemoveGiftProductLines(cart);
        })
        .then(({ removed }) => {
          if (removed) { window.location.reload(); return; }
          applySectionPatch();
        })
        .catch((err) => {
          if (err !== 'reloaded') applySectionPatch();
        });
    })
    .catch(() => {
      window.location.reload();
    })
    .finally(() => {
      isUpdating = false;
      setLoadingState(false, lineIndex);
    });
  }

  /**
   * Get sections to render for cart updates
   * Similar to theme's CartItems.getSectionsToRender()
   */
  function getSectionsToRender() {
    const sections = [];

    // JKL Main Cart section - get section ID from data attribute
    const jklCartPage = document.querySelector('[data-jkl-cart-page][data-section-id]');
    if (jklCartPage && jklCartPage.dataset.sectionId) {
      const sectionId = jklCartPage.dataset.sectionId;

      // Cart lines (products list)
      sections.push({
        id: 'jkl-cart-lines',
        section: sectionId,
        selector: '[data-jkl-cart-lines]'
      });

      // Cart summary (subtotal, total)
      sections.push({
        id: 'jkl-cart-summary',
        section: sectionId,
        selector: '.jkl-cart-page__summary'
      });

      // Free shipping bar
      sections.push({
        id: 'jkl-cart-shipping',
        section: sectionId,
        selector: '.jkl-cart-page__sidebar-shipping'
      });

      // Items count
      sections.push({
        id: 'jkl-cart-items-count',
        section: sectionId,
        selector: '.jkl-cart-page__items-count'
      });

      // Gift threshold promo
      sections.push({
        id: 'jkl-cart-gift-promo',
        section: sectionId,
        selector: '.jkl-cart-page__gift-promo'
      });

      // Gift product promo (category-based — re-renders selected gift state)
      sections.push({
        id: 'jkl-cart-gift-product-promo',
        section: sectionId,
        selector: '.jkl-cart-page__gift-product-promo'
      });
    }

    return sections;
  }

  function updateButtonStates(lineIndex, quantity) {
    const item = document.querySelector(`[data-jkl-cart-item][data-line-index="${lineIndex}"]`);
    if (!item) return;

    const decreaseBtn = item.querySelector('[data-jkl-quantity-decrease]');
    if (decreaseBtn) {
      decreaseBtn.disabled = quantity <= 1;
    }
  }

  // =========================================================================
  // Remove Item
  // =========================================================================

  function initRemoveButtons() {
    document.querySelectorAll('[data-jkl-remove-item]').forEach(btn => {
      btn.addEventListener('click', handleRemoveItem);
    });
  }

  function handleRemoveItem(e) {
    e.preventDefault();

    if (isUpdating) return;

    const btn = e.currentTarget;
    const lineIndex = btn.dataset.lineIndex;

    // Analytics: capture item data from DOM before removal
    const lineEl = document.querySelector(`[data-jkl-cart-item][data-line-index="${lineIndex}"]`);
    if (lineEl && window.gooviAnalytics) {
      const removedItem = {
        title:               lineEl.dataset.analyticsTitle || '',
        product_title:       lineEl.dataset.analyticsProductTitle || '',
        sku:                 lineEl.dataset.analyticsSku || '',
        price:               parseInt(lineEl.dataset.analyticsPrice || '0', 10),
        compare_at_price:    parseInt(lineEl.dataset.analyticsCompareAtPrice || '0', 10),
        quantity:            parseInt(lineEl.dataset.analyticsQuantity || '1', 10),
        category_attributes: lineEl.dataset.analyticsCategoryAttributes || null,
      };
      fetch(CART_ROUTES.get, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          window.gooviAnalytics.pushRemoveFromCart(removedItem, cart);
        })
        .catch(function () {
          window.gooviAnalytics.pushRemoveFromCart(removedItem, null);
        });
    }

    // Use the same approach as quantity update - with section rendering
    executeQuantityUpdate(lineIndex, 0);
  }

  // =========================================================================
  // Add to Cart (Recommendations)
  // =========================================================================

  function initAddToCartButtons() {
    document.querySelectorAll('[data-jkl-add-to-cart]').forEach(btn => {
      btn.addEventListener('click', handleAddToCart);
    });
  }

  function handleAddToCart(e) {
    e.preventDefault();

    if (isUpdating) return;

    const btn = e.currentTarget;
    const variantId = btn.dataset.variantId;
    const quantity = parseInt(btn.dataset.quantity || '1', 10);

    if (!variantId) return;

    isUpdating = true;
    btn.disabled = true;

    let _addedItemData = null;

    fetch(CART_ROUTES.add, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: parseInt(variantId, 10),
          quantity: quantity
        }]
      })
    })
    .then(response => response.json())
    .then(addResponse => {
      // Capture the added item for analytics
      if (addResponse && addResponse.items && addResponse.items.length > 0) {
        _addedItemData = addResponse.items[0];
      }
      // Fetch updated cart
      return fetch(CART_ROUTES.get, {
        headers: { 'Accept': 'application/json' }
      });
    })
    .then(response => response.json())
    .then(cart => {
      // Analytics: add_to_cart from recommendations
      if (_addedItemData && window.gooviAnalytics) {
        window.gooviAnalytics.pushAddToCart(_addedItemData, cart);
      }
      refreshCartUI(cart);
      openCartDrawer();
      refreshShippingBarSection(getJklEligibleCartValueCents(cart));
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
    })
    .finally(() => {
      isUpdating = false;
      btn.disabled = false;
    });
  }

  // Note: initCloseOnLinkClick removed - now handled by event delegation in initCartDrawer

  // =========================================================================
  // UI Updates
  // =========================================================================

  function setLoadingState(loading, lineIndex = null) {
    const drawer = document.querySelector('[data-jkl-cart-drawer]');
    const page = document.querySelector('[data-jkl-cart-page]');

    if (drawer) {
      drawer.classList.toggle('is-loading', loading);
    }
    if (page) {
      page.classList.toggle('is-loading', loading);
    }

    // Show/hide loading overlay for specific line item
    if (lineIndex) {
      const lineItem = document.querySelector(`[data-jkl-cart-item][data-line-index="${lineIndex}"]`);
      if (lineItem) {
        const overlay = lineItem.querySelector('.loading-overlay');
        if (overlay) {
          overlay.classList.toggle('hidden', !loading);
        }
        lineItem.classList.toggle('is-updating', loading);
      }
    }

    // Disable all interactive elements during loading
    const buttons = document.querySelectorAll('[data-jkl-quantity-decrease], [data-jkl-quantity-increase], [data-jkl-remove-item], [data-jkl-add-to-cart]');
    buttons.forEach(btn => {
      if (loading) {
        btn.dataset.wasDisabled = btn.disabled;
        btn.disabled = true;
      } else {
        btn.disabled = btn.dataset.wasDisabled === 'true';
        delete btn.dataset.wasDisabled;
      }
    });
  }

  /**
   * Refresh cart UI after update
   * Updates DOM directly with cart data to avoid extra network requests.
   * Only uses section rendering for major changes (item removal).
   */
  function refreshCartUI(cart, forceFullRefresh = false) {
    // Update cart count in header
    updateCartCount(cart.item_count);

    // Update totals directly from cart data
    updateCartTotals(cart);

    // Update line item prices
    updateLineItemPrices(cart);

    // Only do full refresh if cart is empty or explicitly requested
    if (cart.item_count === 0) {
      // Reload to show empty state
      window.location.reload();
    } else if (forceFullRefresh) {
      // Use section rendering only when necessary (e.g., after item removal)
      refreshCartSections();
    }
  }

  /**
   * Update cart totals directly from cart data
   * Avoids extra network requests for section rendering
   */
  function updateCartTotals(cart) {
    const { displayTotal, displaySubtotal } = getJklDisplayedCartTotals(cart);
    const discountAmount = displaySubtotal - displayTotal;

    // Update subtotal (eligible lines only)
    const subtotalEls = document.querySelectorAll('[data-cart-subtotal]');
    subtotalEls.forEach(el => {
      el.textContent = formatMoney(displaySubtotal);
    });

    // Update total price elements
    const totalEls = document.querySelectorAll('.jkl-cart-summary__total-value, [data-cart-total]');
    totalEls.forEach(el => {
      el.textContent = formatMoney(displayTotal);
    });

    // Update original price if there are discounts
    const originalPriceEls = document.querySelectorAll('.jkl-cart-summary__original-value, [data-cart-original-total]');
    originalPriceEls.forEach(el => {
      el.textContent = formatMoney(displaySubtotal);
    });

    // Update discount amount
    const discountEls = document.querySelectorAll('.jkl-cart-summary__discount-value, [data-cart-discount]');
    discountEls.forEach(el => {
      if (discountAmount > 0) {
        el.textContent = '-' + formatMoney(discountAmount);
        el.closest('.jkl-cart-summary__discount')?.classList.remove('hidden');
      } else {
        el.closest('.jkl-cart-summary__discount')?.classList.add('hidden');
      }
    });
  }

  /**
   * Update individual line item prices after quantity change
   */
  function updateLineItemPrices(cart) {
    cart.items.forEach((item, index) => {
      const lineIndex = index + 1;
      const itemEl = document.querySelector(`[data-jkl-cart-item][data-line-index="${lineIndex}"]`);
      if (!itemEl) return;

      // Update line total
      const lineTotalEl = itemEl.querySelector('[data-line-total]');
      if (lineTotalEl) {
        lineTotalEl.textContent = formatMoney(item.final_line_price);
      }

      // Update quantity display
      const qtyEl = itemEl.querySelector('[data-jkl-quantity-value]');
      if (qtyEl) {
        qtyEl.textContent = item.quantity;
      }
    });
  }

  function updateCartCount(count) {
    // Guard against undefined/null count
    if (count === undefined || count === null) {
      return;
    }

    // Update all cart count elements
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });

    // Update drawer title
    const drawerTitle = document.querySelector('.jkl-cart-drawer__title');
    if (drawerTitle) {
      const titleText = drawerTitle.textContent.replace(/\(\d+\)/, '').trim();
      drawerTitle.textContent = count > 0 ? `${titleText} (${count})` : titleText;
    }
  }

  /**
   * Update shipping bars from the eligible cart total (cents).
   * Reads message templates from data-message-pending / data-message-reached
   * which are locale-translated strings set by Liquid — no hardcoded Italian.
   */
  function updateShippingBarsFromEligibleCents(eligibleCents) {
    document.querySelectorAll('[data-jkl-shipping-bar]').forEach((bar) => {
      const thresholdRaw = parseFloat(bar.dataset.threshold);
      // A promo entry (see jkl-cart-free-shipping-bar.liquid) may legitimately set its
      // own Threshold to 0 to make free shipping apply to any order — only bail out
      // when there's no valid threshold at all (bar shouldn't be in the DOM then anyway).
      if (!(thresholdRaw >= 0)) return;

      const cartTotal   = eligibleCents / 100;
      const remaining   = Math.max(0, thresholdRaw - cartTotal);
      const progress    = thresholdRaw > 0 ? Math.min(100, (cartTotal / thresholdRaw) * 100) : 100;

      const fill = bar.querySelector('[data-jkl-shipping-fill]');
      if (fill) fill.style.maxWidth = `${progress}%`;

      const msg = bar.querySelector('.jkl-cart-shipping__message p');
      if (!msg) return;

      if (remaining > 0) {
        const template   = bar.dataset.messagePending || '';
        const amountHtml = `<span class="jkl-cart-shipping__amount">${formatMoney(remaining * 100)}</span>`;
        msg.innerHTML    = template.replace('||amount||', amountHtml);
      } else {
        msg.textContent = bar.dataset.messageReached || '';
      }

      // Keep data-cart-value in sync so subsequent calls are accurate
      bar.dataset.cartValue = cartTotal.toString();
    });
  }

  /**
   * Refresh drawer shipping bar via Shopify Section Rendering API (Liquid
   * calculation — immune to JS/Liquid exclusion-logic divergence).
   * Falls back to JS calculation using data-* attributes when the fetch fails.
   */
  function refreshShippingBarSection(eligibleCents) {
    fetch('/?sections=jkl-cart-drawer', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((data) => {
        const html = data['jkl-cart-drawer'];
        if (!html) { updateShippingBarsFromEligibleCents(eligibleCents); return; }

        const doc    = new DOMParser().parseFromString(html, 'text/html');
        const newBar = doc.querySelector('[data-jkl-shipping-bar]');
        if (!newBar) { updateShippingBarsFromEligibleCents(eligibleCents); return; }

        // Replace every shipping bar on the page with the Liquid-rendered one
        document.querySelectorAll('[data-jkl-shipping-bar]').forEach((bar) => {
          bar.outerHTML = newBar.outerHTML;
        });
      })
      .catch(() => {
        // Section render failed — fall back to JS calculation
        updateShippingBarsFromEligibleCents(eligibleCents);
      });
  }

  function refreshCartSections() {
    // Use Shopify's section rendering API for smooth updates
    const sections = ['jkl-cart-drawer', 'jkl-main-cart'];
    const sectionIds = sections.map(s => document.querySelector(`[data-section-id="${s}"]`)?.dataset.sectionId).filter(Boolean);

    if (sectionIds.length === 0) {
      // Fallback: reload page
      window.location.reload();
      return;
    }

    fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`)
      .then(response => response.json())
      .then(sections => {
        Object.entries(sections).forEach(([id, html]) => {
          const container = document.querySelector(`[data-section-id="${id}"]`);
          if (container) {
            container.innerHTML = html;
          }
        });
        // Re-initialize after DOM update
        init();
      })
      .catch(() => {
        window.location.reload();
      });
  }

  /**
   * Format money helper
   * Uses Shopify's money format or falls back to simple formatting
   */
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const currency = window.Shopify?.currency?.active || 'EUR';

    // Simple formatting - in production, use Shopify's money formatting
    if (currency === 'EUR') {
      return `€${amount.replace('.', ',')}`;
    }
    return `${currency} ${amount}`;
  }

  // =========================================================================
  // Initialize on DOM ready
  // =========================================================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize after Shopify section rendering
  document.addEventListener('shopify:section:load', init);

  // Gift threshold promo modal functions — defined here (not in snippet) so they survive
  // section re-renders that inject HTML via innerHTML (scripts in innerHTML are not executed).
  window.jklGiftModalOpen = function() {
    var modal = document.getElementById('jkl-gift-modal');
    if (modal) {
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }
  };

  window.jklGiftModalClose = function() {
    var modal = document.getElementById('jkl-gift-modal');
    if (modal) {
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  };

  window.jklGiftModalAddGift = function(btn) {
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    btn.disabled = true;
    var originalText = btn.textContent;
    btn.textContent = '...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1, properties: { '_type': 'giftThreshold' } }]
      })
    })
    .then(function(response) { return response.json(); })
    .then(function(data) {
      if (data.status) {
        btn.disabled = false;
        btn.textContent = originalText;
        return;
      }
      window.location.reload();
    })
    .catch(function() {
      btn.disabled = false;
      btn.textContent = originalText;
    });
  };

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') { window.jklGiftModalClose(); }
  });

  // Gift product promo modal functions — defined here (not in snippet) so they survive
  // section re-renders that inject HTML via innerHTML (scripts in innerHTML are not executed).
  window.jklGiftProductModalOpen = function() {
    var modal = document.getElementById('jkl-gift-product-modal');
    if (modal) {
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }
  };

  window.jklGiftProductModalClose = function() {
    var modal = document.getElementById('jkl-gift-product-modal');
    if (modal) {
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  };

  window.jklGiftProductModalAddGift = function(btn) {
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    btn.disabled = true;
    var originalText = btn.textContent;
    btn.textContent = '...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1, properties: { '_type': 'giftProduct' } }]
      })
    })
    .then(function(response) { return response.json(); })
    .then(function(data) {
      if (data.status) { btn.disabled = false; btn.textContent = originalText; return; }
      window.location.reload();
    })
    .catch(function() { btn.disabled = false; btn.textContent = originalText; });
  };

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') { window.jklGiftProductModalClose(); }
  });

  // Expose API for external use
  window.JKLCart = {
    open: openCartDrawer,
    close: closeCartDrawer,
    refresh: () => fetch(CART_ROUTES.get, { headers: { 'Accept': 'application/json' } })
      .then(r => r.json())
      .then(refreshCartUI),
    enforceGiftThresholds: () =>
      fetch(CART_ROUTES.get, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => tryRemoveExcessGiftThresholdLines(cart))
        .then(({ removed }) => {
          if (removed) window.location.reload();
        }),
    enforceGiftProductPromo: () =>
      fetch(CART_ROUTES.get, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => tryRemoveGiftProductLines(cart))
        .then(({ removed }) => {
          if (removed) window.location.reload();
        })
  };

})();
